import { create } from 'zustand';

import { User } from '@features/auth/services/authService';
import { Customers } from '@gql/base-types';
import type { FeatureConfig } from '@features/userManagement/types';

export type HopDriveStore = {
  user?: User;
  rooftop?: Customers;
  customerConfig?: any;
  // Whether the initial customer-config resolution has settled for the current user (success,
  // empty, or error). Config-gated routes register off `customerConfig`, which loads async after
  // the router mounts — this flag lets the NoMatch fallback wait instead of flashing "No match".
  customerConfigLoaded: boolean;
  organizationConfig?: any;
  permissions: string[];
  permissionsLoaded: boolean;
  featureConfigs: Record<string, FeatureConfig | null>;
  // The header selector's rooftops by tier, for tier-aware feature permissions across a user's rooftops. Cross-rooftop `customers.tier` read (named exception): the product-access snapshot covers only the working rooftop, and this decision spans every rooftop the user is linked to.
  customerTierMap: Record<number, string>;
  // The rooftops the header selector fetched for this user (power roles only —
  // stays [] for plain dealers). The list is the access check for programmatic
  // rooftop switches (e.g. search's switch-on-click).
  rooftops: Customers[];
};

export const useStore = create<HopDriveStore>(() => ({
  user: undefined,
  rooftop: undefined,
  customerConfig: undefined,
  customerConfigLoaded: false,
  organizationConfig: undefined,
  permissions: [],
  permissionsLoaded: false,
  featureConfigs: {},
  customerTierMap: {},
  rooftops: [],
}));

export const getNonReactiveState = (): HopDriveStore => useStore.getState();

export const useUser = () => useStore(state => state.user);
export const setUser = (user?: User) => useStore.setState({ user });

export const useRooftop = () => useStore(state => state.rooftop);
export const setRooftop = (rooftop?: Customers) => useStore.setState({ rooftop });

export const useCustomerConfig = () => useStore(state => state.customerConfig);
export const setCustomerConfig = (customerConfig?: any) => useStore.setState({ customerConfig });

export const useCustomerConfigLoaded = () => useStore(state => state.customerConfigLoaded);
export const setCustomerConfigLoaded = (customerConfigLoaded: boolean) =>
  useStore.setState({ customerConfigLoaded });

export const useOrganizationConfig = () => useStore(state => state.organizationConfig);
export const setOrganizationConfig = (organizationConfig?: any) => useStore.setState({ organizationConfig });

// The products slice lives in @hopdrive/feature-portal-products, which owns the model that
// fills it; these are the same accessors, re-exported so every `@store` consumer here is
// unchanged. The package's slice is a zustand store of its own, so it is NOT part of
// `HopDriveStore` and `useStore.setState` cannot reach it — use `resetPortalProducts` /
// `portalProductsStore` for what used to be a `useStore.setState({ productsLoaded })`.
export {
  portalProductsStore,
  resetPortalProducts,
  retryProducts,
  setPortalProducts,
  setProductInputs,
  setProductsFailed,
  setProductsLoaded,
  usePortalProductsState,
  useProductInputs,
  useProductsFailed,
  useProductsLoaded,
  useProductsRetryNonce,
} from '@hopdrive/feature-portal-products';

export const usePermissions = () => useStore(state => state.permissions);
export const setPermissions = (permissions: string[], permissionsLoaded = true) =>
  useStore.setState({ permissions, permissionsLoaded });
export const clearPermissions = () => useStore.setState({ permissions: [], permissionsLoaded: false });

export const useFeatureConfigs = () => useStore(state => state.featureConfigs);
export const setFeatureConfigs = (featureConfigs: Record<string, FeatureConfig | null>) =>
  useStore.setState({ featureConfigs });

export const useCustomerTierMap = () => useStore(state => state.customerTierMap);
export const setCustomerTierMap = (customerTierMap: Record<number, string>) =>
  useStore.setState({ customerTierMap });

export const useRooftops = () => useStore(state => state.rooftops);
export const setRooftops = (rooftops: Customers[]) => useStore.setState({ rooftops });
